(ns minorodata.components.pipeline-editor.details.extracts
  (:require
   ["react" :as react]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.components.log.table :refer [logs-row]]
   [minorodata.components.pipeline-editor.details.utils :refer [selected-node-bar]]
   [minorodata.components.pipeline-editor.utils :refer [extract-edit-href
                                                        extract-legacy-href]]
   [minorodata.components.spinner.core :as spinner]
   [minorodata.logs.model :refer [->logs-view]]
   [minorodata.sandbox.events :refer [reset-preview]]
   [re-posh.core :as re-posh]
   [ui.alert :as alert]
   [ui.button :as button]
   [ui.fields :as fields]
   [ui.table :refer [table tbody th thead tr]]
   [ui.tabs :as tabs]
   [ui.typography :as typography]))

(def log-params
  {:items-per-page 50
   :page 0})

(defn recent-logs' [{:keys [extract timezone logs loading? sandbox?]}]
  (react/useEffect
   (fn []
     (dispatch [:logs-view/add (->logs-view (assoc log-params :term (:name extract)))
                {:replace true}])
     (dispatch [:logs.api/fetch])

     (fn []
       (dispatch-sync [:logs-view/delete])))
   #js [extract])

  (cond
    loading?
    [:div {:class "flex gap-2 items-center px-10 py-6"}
     [spinner/spinner-lucide] "Loading logs..."]

    (empty? logs)
    [typography/p {:class "text-kleene-text-secondary px-10 py-6"} "No recent logs"]

    :else
    [:<>
     (when sandbox?
       [alert/alert {:variant :warning}
        [alert/alert-title "Live extract logs are being displayed."]])
     [table {:data-cy       "logs-table"
             :sticky-header true}
      [thead
       [tr {:sticky true}
        [th {:style {:width "180px" :height "52px"}
             :class "border-r"}
         [:span
          "Date"
          [:div {:class "-mt-1"}
           [:span {:class "text-xs text-kleene-dark-05 dark:text-kleene-dark-03"} timezone]]]]
        [th {:style {:width "80px"}
             :class "border-r"}
         "Type"]
        [th {:class "border-r"}
         "Message"]
        [th {:style {:width "80px"}} "Task"]]]
      [tbody {}
       (for [message-id logs]
         ^{:key message-id}
         [logs-row {:message-id   message-id
                    :skip-columns ["source-name" "name"]
                    :interactive  false}])]]]))

(defn recent-logs [{:keys [extract]}]
  (let [logs     @(re-posh/subscribe [:logs-pager/prop :logs-pager/messages])
        loading? @(re-posh/subscribe [:mold-http/loading? :logs.api/fetch])
        timezone @(re-posh/subscribe [:app/prop :app/timezone])
        sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [:f> recent-logs' {:extract extract
                       :logs      logs
                       :loading?  loading?
                       :timezone  timezone
                       :sandbox?  sandbox?}]))

(defn extract-description-list [{:keys [extract]}]
  (let [extract-details-drawer? @(re-posh/subscribe [:unleash/is-enabled "fe.extract-details-drawer"])]
    [:div {:class "px-10 py-6"}
     [typography/h2 "Extract details"]
     [:div {:class "mt-4 grid grid-cols-[200px_1fr] items-center max-w-xl gap-2"}
      [typography/label "Name"]
      [fields/input {:default-value (:name extract)
                     :disabled  true}]
      [typography/label "Source name"]
      [fields/input {:default-value (get-in extract [:source :name] nil)
                     :disabled  true}]
      [typography/label "Source type"]
      [fields/input {:default-value (get-in extract [:source :source-type] nil)
                     :disabled  true}]
      [typography/label "Destination table"]
      [fields/input {:default-value (get-in extract [:output-tables 0] nil)
                     :disabled  true}]
      [:div {:class "col-span-2 flex mt-2 justify-end"}
       [button/button {:component :a
                       :href (if extract-details-drawer?
                               (extract-edit-href (:source-id extract) (:extract-id extract))
                               (extract-legacy-href (:source-id extract) (:extract-id extract)))}
        "Edit extract"]]]]))

(defn extract-details-tabs' [{:keys [extract master-id tab-request]}]
  (let [[tab set-tab] (react/useState "details")]

    (react/useEffect
     (fn [] (set-tab "details"))
     #js [master-id])

    ;; A tab asked for from outside — the canvas's `Preview → Logs` (see
    ;; `pipeline-editor.dag/preview!`). Same shape as the transform pane's: declared
    ;; after the reset so it wins in a shared commit, consumed once applied, and only
    ;; when addressed to this pane's node.
    (react/useEffect
     (fn []
       (when (and tab-request (= (:id tab-request) master-id))
         (set-tab (:tab tab-request))
         (dispatch [:pipeline-editor.events/request-details-tab nil]))
       js/undefined)
     #js [tab-request master-id])

    [tabs/tabs-root {:value           tab
                     :on-value-change set-tab
                     :class "grid grid-rows-[auto_1fr] overflow-hidden"}
     [tabs/tabs-list {:variant :tab :class "px-10"}
      [tabs/tabs-trigger {:value "details" :variant :tab} "Details"]
      [tabs/tabs-trigger {:value "logs" :variant :tab} "Logs"]]

     [tabs/tabs-content {:value "details" :class "overflow-y-auto"}
      [extract-description-list {:extract extract}]]
     [tabs/tabs-content {:value "logs" :class "overflow-y-auto"}
      [recent-logs {:extract extract}]]]))

(defn extract-details-tabs [props]
  (let [tab-request @(re-posh/subscribe [:pipeline-editor.subs/details-tab-request])]
    [:f> extract-details-tabs' (assoc props :tab-request tab-request)]))

(defn selected-extract-bar [{:keys [selected data loading]}]
  (let [sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [selected-node-bar {:selected selected
                        :loading  loading
                        :name     (or (:name data)
                                      (when-not loading "Extract doesn't exist"))
                        :sandbox? sandbox?}]))

(defn extract-details' [{:keys [selected extract loading sandbox?]}]
  (let [active-extract-id @(re-posh/subscribe [:connections.subs/active-extract-id (:id selected)])]
    ;; A single click on a DAG node only previews it (no tab activation), so the
    ;; extract isn't in the connections store yet. Resolve the current extract-id
    ;; from the stable master-id first and fetch that record directly; if it
    ;; hasn't resolved yet, fall back to the paginated list fetch to seed the
    ;; store (this can still miss the extract on sources with many extracts).
    (react/useEffect
     (fn []
       (when-let [source-id (:group-id selected)]
         (if active-extract-id
           (dispatch [:connections.api/fetch-extract {:source-id  source-id
                                                       :extract-id active-extract-id}])
           (dispatch [:connections.api/fetch-extracts {:source-id source-id}])))
       js/undefined)
     #js [(:group-id selected) (:id selected) active-extract-id])

    [:div {:class "grid h-full grid-rows-[auto_1fr] overflow-hidden"}
     [selected-extract-bar {:selected selected
                            :data extract
                            :loading loading}]
     (cond
       (not-empty extract)
       [extract-details-tabs {:extract extract
                              :master-id (:id selected)
                              :source-id (:group-id selected)}]

       loading
       [:div {:class "flex items-center justify-center h-full"}
        [spinner/with-text {:text "Loading extract..."}]]

       :else
       [:div {:class "flex flex-col gap-2 items-center justify-center h-full"}
        [typography/h2 "Extract doesn't exist in " (if sandbox? "sandbox" "live") " environment"]
        (when (:preview selected)
          [button/button {:variant :secondary
                          :on-click #(dispatch [:app.events/navigate-query reset-preview])}
           "Close preview"])])]))

(defn extract-details [{:keys [selected]}]
  (let [extract @(re-posh/subscribe [:pipeline-editor.subs/extract (:id selected)])
        loading (or @(re-posh/subscribe [:mold-http/loading? :dag.api/fetch-graph])
                    @(re-posh/subscribe [:mold-http/loading-id? :connections.api/fetch-extracts
                                         (keyword (:group-id selected))])
                    @(re-posh/subscribe [:mold-http/loading-id? :connections.api/fetch-extract
                                         (keyword (:group-id selected))]))
        sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [:f> extract-details' {:selected selected
                           :extract  extract
                           :loading  loading
                           :sandbox? sandbox?}]))